import { useQuery } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Autocomplete,
    Box,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    GetAddEditRosterOptionsQuery,
    GetCompanySpecificOptionsQuery,
    GetCompaniesByNameQuery,
    ProcessStatus,
    RosterStatus,
} from '__generated__/graphql'
import { FC, useEffect, useRef, useState } from 'react'
import { rosterStatusOptions } from 'recruiter/types/RosterStatuses'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import { getFullName, getPxAsRem } from 'shared/utils'
import {
    GetAddEditRosterOptions,
    GetCompanySpecificOptions,
} from '../RosterDrawer/AddOrEdit.gql'

export interface BulkUploadRosterConfig {
    rosterName: string
    rosterStatus: RosterStatus
    companyId?: string
    companyOfficeIds?: string[]
    industryIds?: string[]
    sectorIds?: string[]
    clientContactIds?: string[]
    initialCandidateProcessStatusId?: string
    positions?: { titleId: string; placementCount: number }[]
    startDate?: string
}

interface RosterConfigFormProps {
    onChange: (config: BulkUploadRosterConfig | null) => void
}

type Company = GetCompaniesByNameQuery['companiesByName']['data'][number] | null
type StatusOption = (typeof rosterStatusOptions)[number]
type ProcessStatusOption =
    | GetAddEditRosterOptionsQuery['processStatuses'][number]
    | null
type TitleOption = GetAddEditRosterOptionsQuery['titles'][number]
type IndustryOption = GetAddEditRosterOptionsQuery['industries'][number]
type SectorOption = GetAddEditRosterOptionsQuery['sectors'][number]
type EmployeeOption = NonNullable<
    GetCompanySpecificOptionsQuery['company']['employees']
>[number]
type OfficeOption = NonNullable<
    GetCompanySpecificOptionsQuery['company']['companyOffices']
>[number]

const RosterConfigForm: FC<RosterConfigFormProps> = ({ onChange }) => {
    const theme = useTheme()

    const [rosterName, setRosterName] = useState('')
    const [rosterStatus, setRosterStatus] = useState<StatusOption>({
        id: RosterStatus.Active,
        label: 'Active',
    })
    const [company, setCompany] = useState<Company>(null)
    const [clientContacts, setClientContacts] = useState<EmployeeOption[]>([])
    const [processStatus, setProcessStatus] =
        useState<ProcessStatusOption>(null)
    const [titles, setTitles] = useState<TitleOption[]>([])
    const [positionCount, setPositionCount] = useState<Record<string, number>>(
        {}
    )
    const [industries, setIndustries] = useState<IndustryOption[]>([])
    const [sectors, setSectors] = useState<SectorOption[]>([])
    const [locations, setLocations] = useState<OfficeOption[]>([])
    const [startDate, setStartDate] = useState<string | null>(null)

    const { data: options } = useQuery(GetAddEditRosterOptions, {
        onCompleted: (data) => {
            const noStatus = data.processStatuses.find(
                (status) => status.status === ProcessStatus.NoStatus
            )
            if (noStatus) setProcessStatus(noStatus)
        },
    })

    const { data: companySpecificOptions } = useQuery(
        GetCompanySpecificOptions,
        {
            variables: { companyId: company?.id || '' },
            skip: !company?.id,
        }
    )

    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    useEffect(() => {
        if (!rosterName.trim()) {
            onChangeRef.current(null)
            return
        }

        const config: BulkUploadRosterConfig = {
            rosterName: rosterName.trim(),
            rosterStatus: rosterStatus.id,
        }

        if (company?.id) config.companyId = company.id
        if (clientContacts.length > 0)
            config.clientContactIds = clientContacts.map((c) => c.id)
        if (processStatus?.id)
            config.initialCandidateProcessStatusId = processStatus.id
        if (titles.length > 0) {
            config.positions = titles.map((t) => ({
                titleId: t.id,
                placementCount: positionCount[t.id] || 1,
            }))
        }
        if (industries.length > 0)
            config.industryIds = industries.map((i) => i.id)
        if (sectors.length > 0) config.sectorIds = sectors.map((s) => s.id)
        if (locations.length > 0)
            config.companyOfficeIds = locations.map((l) => l.id)
        if (startDate) config.startDate = startDate

        onChangeRef.current(config)
    }, [
        rosterName,
        rosterStatus,
        company,
        clientContacts,
        processStatus,
        titles,
        positionCount,
        industries,
        sectors,
        locations,
        startDate,
    ])

    return (
        <Stack sx={{ gap: getPxAsRem(24), mt: getPxAsRem(16) }}>
            {/* NAME */}
            <TextField
                label="Search Name"
                fullWidth
                required
                value={rosterName}
                onChange={(e) => setRosterName(e.target.value)}
                error={rosterName.length > 0 && !rosterName.trim()}
                helperText={
                    rosterName.length > 0 && !rosterName.trim()
                        ? 'Search name is required'
                        : ''
                }
            />

            {/* SEARCH STATUS */}
            <Autocomplete
                id="rosterStatus"
                options={rosterStatusOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.label}
                value={rosterStatus}
                onChange={(_, value) => {
                    if (value) setRosterStatus(value)
                }}
                renderInput={(params) => (
                    <TextField {...params} label="Search Status" />
                )}
            />

            {/* COMPANY */}
            <CompaniesAutocomplete
                id="company"
                getOptionLabel={(option) => option.value}
                value={company}
                onChange={(_, value) => {
                    setCompany(value)
                    setClientContacts([])
                    setLocations([])
                }}
            />

            {/* CLIENT CONTACTS */}
            <Autocomplete
                multiple
                id="clientContacts"
                options={companySpecificOptions?.company.employees || []}
                getOptionLabel={(option) =>
                    option?.user ? getFullName(option.user) : '-'
                }
                renderInput={(params) => (
                    <TextField {...params} label="Client Contacts" />
                )}
                value={clientContacts}
                onChange={(_, value) => setClientContacts(value)}
            />

            {/* CANDIDATE PROCESS STATUS */}
            <Autocomplete
                id="processStatus"
                options={options?.processStatuses || []}
                groupBy={(option) =>
                    option.processStatusSummaryGroup?.label || ''
                }
                getOptionLabel={(option) => option.statusLabel}
                value={processStatus}
                onChange={(_, value) => setProcessStatus(value)}
                renderInput={(params) => (
                    <TextField {...params} label="Candidate Process Status" />
                )}
            />

            <Divider />

            {/* OPENINGS */}
            <Typography variant="h5">Openings</Typography>

            <Typography id="positionsLabel" variant="subtitle2">
                Select all the different positions to be placed on this roster.
            </Typography>
            <Autocomplete
                multiple
                id="titles"
                options={options?.titles || []}
                getOptionLabel={(option) => option.value}
                value={titles}
                onChange={(_, value) => setTitles(value)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        hiddenLabel
                        aria-labelledby="positionsLabel"
                    />
                )}
            />
            {titles.length > 0 && (
                <>
                    <Typography id="positionsCountLabel" variant="subtitle2">
                        Add the number of candidates that need to be placed in
                        each position.
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: getPxAsRem(24),
                            flexWrap: 'wrap',
                        }}
                    >
                        {titles.map((position) => (
                            <TextField
                                key={position.id}
                                label={position.value}
                                InputProps={{ inputProps: { min: 1 } }}
                                type="number"
                                sx={{ maxWidth: getPxAsRem(140) }}
                                value={
                                    positionCount[position.id]?.toString() || 1
                                }
                                onChange={(e) =>
                                    setPositionCount((prev) => ({
                                        ...prev,
                                        [position.id]: +e.target.value,
                                    }))
                                }
                            />
                        ))}
                    </Box>
                    <Typography variant="h6">
                        {`Total Openings = `}
                        <Box
                            sx={{
                                display: 'inline',
                                color: theme.palette.primary.main,
                            }}
                        >
                            {titles.reduce(
                                (acc, val) =>
                                    acc + (positionCount[val.id] || 1),
                                0
                            )}
                        </Box>
                    </Typography>
                </>
            )}

            <Divider />

            {/* INDUSTRIES */}
            <Autocomplete
                multiple
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={options?.industries || []}
                getOptionLabel={(option) => option.value}
                value={industries}
                onChange={(_, value) => setIndustries(value)}
                renderInput={(params) => (
                    <>
                        <Typography
                            variant="h5"
                            id="industry-label"
                            sx={{ marginBottom: getPxAsRem(8) }}
                        >
                            Industry
                        </Typography>
                        <TextField
                            {...params}
                            aria-labelledby="industry-label"
                        />
                    </>
                )}
            />
            <Divider />

            {/* SECTORS */}
            <Autocomplete
                multiple
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={options?.sectors || []}
                getOptionLabel={(option) => option.value}
                value={sectors}
                onChange={(_, value) => setSectors(value)}
                renderInput={(params) => (
                    <>
                        <Typography
                            variant="h5"
                            id="sector-label"
                            sx={{ marginBottom: getPxAsRem(8) }}
                        >
                            Sector
                        </Typography>
                        <TextField {...params} aria-labelledby="sector-label" />
                    </>
                )}
            />
            <Divider />

            {/* LOCATIONS */}
            <Autocomplete
                multiple
                isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                }
                options={companySpecificOptions?.company.companyOffices || []}
                getOptionLabel={(option) => option.officeLocation?.value || ''}
                value={locations}
                onChange={(_, value) => setLocations(value)}
                renderInput={(params) => (
                    <>
                        <Typography
                            variant="h5"
                            id="location-label"
                            sx={{ marginBottom: getPxAsRem(8) }}
                        >
                            Location
                        </Typography>
                        <TextField
                            {...params}
                            aria-labelledby="location-label"
                        />
                    </>
                )}
            />
            <Divider />

            {/* START DATE */}
            <Autocomplete
                options={options?.rosterStartDates || []}
                value={startDate}
                onChange={(_, value) => setStartDate(value)}
                renderInput={(params) => (
                    <>
                        <Typography
                            variant="h5"
                            id="startDate-label"
                            sx={{ marginBottom: getPxAsRem(8) }}
                        >
                            Start Date
                        </Typography>
                        <TextField
                            {...params}
                            aria-labelledby="startDate-label"
                        />
                    </>
                )}
            />
        </Stack>
    )
}

export default RosterConfigForm
